import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import { printDressDeclaration } from '../../utils/printDressDeclaration';
import './Salon.css';

const PrintDeclarationModal = ({ isOpen, onClose, salonId, salon, dress }) => {
  const { t, getMonthName } = useLanguage();
  const { formatPrice } = useSalon();
  const [declarations, setDeclarations] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  const fetchDeclarations = useCallback(async () => {
    if (!salonId) return;
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/declarations?active_only=true&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data.declarations || [];
      setDeclarations(list);
      if (list.length > 0) {
        setSelectedTemplateId(String(list[0].id));
      } else {
        setSelectedTemplateId('');
      }
    } catch (err) {
      console.error('Error fetching declaration templates:', err);
      setError(t('messages.error.failedToFetchDeclarationTemplates', 'Failed to load declaration templates'));
    } finally {
      setLoadingTemplates(false);
    }
  }, [salonId, t]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      fetchDeclarations();
    }
  }, [isOpen, fetchDeclarations]);

  const handlePrint = async () => {
    if (!selectedTemplateId) {
      setError(t('messages.error.selectDeclarationFirst', 'Please select a declaration template'));
      return;
    }
    if (!dress?.id) return;

    try {
      setPrinting(true);
      setError('');

      const token = localStorage.getItem('token');
      const dressResponse = await axios.get(`/api/salons/${salonId}/dresses/${dress.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullDress = dressResponse.data.dress || dressResponse.data;

      await printDressDeclaration({
        salonId,
        templateId: selectedTemplateId,
        dress: fullDress,
        t,
        formatPrice,
        getMonthName,
        salonSlug: salon?.slug
      });
      onClose();
    } catch (err) {
      console.error('Error printing declaration:', err);
      setError(err.message || t('messages.error.failedToPrintDeclaration', 'Failed to print declaration'));
    } finally {
      setPrinting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('dresses.printDeclaration', 'Print declaration')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {dress?.name && (
            <p style={{ marginBottom: '16px' }}>
              <strong>{dress.name}</strong>
              {dress.brand_name ? ` — ${dress.brand_name}` : ''}
            </p>
          )}
          {error && <div className="error-message">{error}</div>}
          {loadingTemplates ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('dresses.loadingDeclaration', 'Loading templates...')}</p>
            </div>
          ) : declarations.length === 0 ? (
            <p>{t('dresses.noDeclarationTemplates', 'No active declaration templates. Create one in Settings.')}</p>
          ) : (
            <div className="form-group">
              <label htmlFor="declaration_template_select">{t('dresses.selectDeclarationTemplate', 'Declaration template')}</label>
              <select
                id="declaration_template_select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                {declarations.map((decl) => (
                  <option key={decl.id} value={decl.id}>{decl.title}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={printing}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="salon-button primary"
            onClick={handlePrint}
            disabled={printing || loadingTemplates || declarations.length === 0}
          >
            {printing ? t('dresses.loadingDeclaration', 'Loading...') : t('dresses.printDeclaration', 'Print declaration')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintDeclarationModal;
